import { APP_ID, ErrorHandler, NgModule } from '@angular/core';

// FIREBASE MODULES
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { initializeAppCheck, provideAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import {
    Auth,
    browserLocalPersistence,
    browserSessionPersistence,
    indexedDBLocalPersistence,
    initializeAuth,
    provideAuth,
} from '@angular/fire/auth';
import { FIREBASE_OPTIONS } from '@angular/fire/compat';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';

import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

// MATERIAL MODULES
import { MatTooltipModule } from '@angular/material/tooltip';

// THIRD PARTY MODULES
import { NgxEssentiaModule, NgxLoggerCustomServerService } from 'ngx-essentia';
import { LoggerModule, NGXLogger, NgxLoggerLevel, TOKEN_LOGGER_SERVER_SERVICE } from "ngx-logger";


// APP MODULES AND COMPONENTS
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { Error404Page } from './app-shell/errors/404.component';
import { CreatePollButton } from './shared/create-poll-button/create-poll.button';
import { NavBarComponent } from './app-shell/nav/nav-bar/nav-bar.component';
import { NavMenuButtonComponent } from './shared/menu-button/menu-button.component';
import { appRoutes } from './routes';
import { LogoComponent } from './shared/logo/logo.component';
import { LanguageBannerComponent } from './app-shell/shared/language-banner/language-banner.component';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { CustomDateAdapter } from './shared/helpers/custom-date-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { WhooAppComponent } from './whoo/app-component/whoo-app.component';
import { FeedbackButtonComponent } from './shared/feedback-button/feedback-button.component';
import { StickyBarComponent } from './shared/sticky-bar/sticky-bar.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MarkdownModule } from 'ngx-markdown';
import { HttpClientModule, provideHttpClient } from '@angular/common/http';
import { CookieConsentComponent } from './app-shell/shared/cookie-consent/cookie-consent.component';
import { USE_EMULATOR as USE_AUTH_EMULATOR } from '@angular/fire/compat/auth';
import { USE_EMULATOR as USE_DATABASE_EMULATOR } from '@angular/fire/compat/database';
import { USE_EMULATOR as USE_FIRESTORE_EMULATOR } from '@angular/fire/compat/firestore';
import { USE_EMULATOR as USE_FUNCTIONS_EMULATOR } from '@angular/fire/compat/functions';
import { USE_EMULATOR as USE_STORAGE_EMULATOR } from '@angular/fire/compat/storage';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './shared/translation/transloco-http-loader';
import { TranslateMigratedPipe } from './shared/translation/trans-migrated.pipe';
import { CHUNK_RELOAD_NAVIGATION_TRACKER, ChunkErrorHandler } from './chunk-error-handler';
import { LANGUAGE_REDIRECT_INITIALIZER } from './shared/services/language-redirect.initializer';
import { APP_CHECK_TELEMETRY_INITIALIZER } from './whoo/core/app-check-telemetry.service';
import { authStorageProbe } from './user/auth-storage-probe';


@NgModule({
    imports: [
        BrowserModule,
        RouterModule.forRoot(appRoutes, {
            scrollPositionRestoration: 'enabled',
        }),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        // App Check (reCAPTCHA Enterprise) fuer die Realtime Database - muss VOR provideAuth/provideDatabase/
        // provideStorage stehen, damit die SDKs das Token mitschicken (Workorder app-check-rtdb 2026-10-26).
        provideAppCheck((injector) => {
            const siteKey = environment.appCheck?.siteKey;
            const debugGlobal = self as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string | boolean };
            if (environment.appCheck?.debugToken) {
                // einzige Stelle im App-Code, die das Global setzt - derzeit setzt kein Environment einen Wert (lokal ist App Check aus)
                debugGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN = environment.appCheck.debugToken;
            } else if (debugGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN === true) {
                // AngularFire setzt das Global in Dev-Mode-Builds (isDevMode, also auch auf www.whocan.dev) selbst
                // auf true; das SDK wuerde dann einen unregistrierten Zufalls-Token statt reCAPTCHA tauschen und
                // jeder Request waere unverifiziert. Ein String-Token (Playwright, scripts/lib/app-check-debug.mjs)
                // bleibt stehen.
                delete debugGlobal.FIREBASE_APPCHECK_DEBUG_TOKEN;
            }
            if (!siteKey) {
                // die App darf nie an fehlender Konfiguration scheitern - ohne App Check weiter wie bisher
                try {
                    injector.get(NGXLogger).warn('AppCheck: no siteKey configured, skipping');
                } catch {
                    console.warn('AppCheck: no siteKey configured, skipping');
                }
                return undefined;
            }
            return initializeAppCheck(getApp(), {
                provider: new ReCaptchaEnterpriseProvider(siteKey),
                // explizit, sonst haengt der Refresh an automaticDataCollectionEnabled
                isTokenAutoRefreshEnabled: true,
            });
        }),
        provideAuth(() => {
            // Die eine Auth-Instanz der App: AngularFireAuth (compat) und firebase.auth() uebernehmen sie, weil sie
            // hier zuerst initialisiert wird (workorder-auth-persistence-local-first-2026-10-31, Task 0).
            // localStorage ZUERST (seit 2026-10-31): auf WebKit (iPhone/iPad, 86 % der dauerhaften Auth-Drops)
            // verschwand der gespeicherte Nutzer aus IndexedDB, und das SDK loggt ihn daraufhin selbst aus.
            // Bestehende IndexedDB-Sitzungen wandern beim ersten Start mit derselben uid nach localStorage
            // (PersistenceUserManager.create sucht in der ganzen Hierarchie und migriert in die erste verfuegbare).
            // Faellt localStorage aus (blockierter Speicher), greifen IndexedDB und Session wie bisher.
            authStorageProbe.count('authInits'); // Zaehler fuer den Doppel-Bootstrap-Check (auth-drop-ios-diagnostics, Task 0.4)
            const auth: Auth = initializeAuth(getApp(), {
                persistence: [
                    browserLocalPersistence,
                    indexedDBLocalPersistence,
                    browserSessionPersistence,
                ],
                popupRedirectResolver: undefined,
            });
            return auth;
        }),
        provideFirestore(() => getFirestore()),
        provideDatabase(() => getDatabase()),
        provideStorage(() => getStorage()),
        MatTooltipModule,
        BrowserAnimationsModule,

        MarkdownModule.forRoot(),
        HttpClientModule,

        NgxEssentiaModule.init({
            isDebug: environment.debug_log,
            loggerPrefix: 'WHOCAN-LIB',
            appName: environment.brandName,
            version: environment.appVersion,
            host: environment.host
        }),
        LoggerModule.forRoot({
            serverLoggingUrl: '/uilogging',
            level: environment.ngxLoggerLevel,
            serverLogLevel: environment.ngxServerLoggerLevel,
            enableSourceMaps: true
        },
            {
                serverProvider: {
                    provide: TOKEN_LOGGER_SERVER_SERVICE, useClass: NgxLoggerCustomServerService
                }
            }
        ),
        ServiceWorkerModule.register('ngsw-worker.js', {
            enabled: environment.enableServiceWorker,
            // Register the ServiceWorker immediatly because the angular standard "registerWhenStable:30000"
            // seems not to work with lighthaouse
            // getting error "Es wurde kein Service Worker erkannt, der die Seite und start_url kontrolliert"
            registrationStrategy: 'registerImmediately',
        }),
        // STANDALONE COMPONENTS
        CreatePollButton,
        NavMenuButtonComponent,
        LogoComponent,
        LanguageBannerComponent,
        WhooAppComponent,
        NavBarComponent,
        FeedbackButtonComponent,
        StickyBarComponent,
        CookieConsentComponent,
        TranslateMigratedPipe

    ],
    declarations: [
        // MAIN COMPONENT
        AppComponent,
        // 404 NOT FOUND
        Error404Page,
        // INDEX PAGE
    ],
    bootstrap: [AppComponent],
    providers:
        [

            { provide: ErrorHandler, useClass: ChunkErrorHandler },
            CHUNK_RELOAD_NAVIGATION_TRACKER,
            // Muss vor dem Router-Start laufen (siehe Kommentar im Initializer).
            LANGUAGE_REDIRECT_INITIALIZER,
            // meldet App-Check-Token-Fehler einmal pro Session als warn nach /uilogging (Entscheid 6)
            APP_CHECK_TELEMETRY_INITIALIZER,
            provideHttpClient(),
            provideTransloco({
                config: {
                    availableLangs: ['en', 'de', 'fr', 'it', 'es', 'pt'],
                    defaultLang: 'en',
                    fallbackLang: 'en',
                    reRenderOnLangChange: false,
                    prodMode: environment.production,
                    missingHandler: {
                        allowEmpty: true,
                        logMissingKey: false,
                        useFallbackTranslation: true,
                    },
                    scopes: {
                        keepCasing: true
                    }
                },
                loader: TranslocoHttpLoader,
            }),
            {
                provide: FIREBASE_OPTIONS,
                useValue: environment.firebase,
            },
            {
                provide: MAT_RADIO_DEFAULT_OPTIONS,
                useValue: { color: 'primary' },
            },
            {
                provide: MAT_DATE_LOCALE,
                useValue: 'en-GB',
            },
            {
                provide: APP_ID,
                useValue: 'serverApp',
            },
            // https://github.com/angular/angularfire/blob/master/docs/compat/emulators/emulators.md
            { provide: USE_AUTH_EMULATOR, useValue: environment.useEmulators ? ['http://localhost:9099'] : undefined },// https://www.reddit.com/r/Angular2/comments/piiwvc/angular_not_working_with_firebase_emulators/
            { provide: USE_DATABASE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 9001] : undefined },
            //  { provide: USE_FIRESTORE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 8080] : undefined },
            { provide: USE_FUNCTIONS_EMULATOR, useValue: environment.useEmulators ? ['localhost', 5001] : undefined },
            { provide: USE_STORAGE_EMULATOR, useValue: environment.useEmulators ? ['localhost', 9199] : undefined },

        ],
})
export class AppModule { }
